import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Forecast,
  ForecastDetail,
  ForecastMetric,
  ForecastPort,
  ForecastWatchPage,
} from '../../consumer/forecasting';
import { riskHref, type RiskPageProps } from '../../consumer/workbench';
import './details.css';
import {
  DetailReadNotice,
  Fields,
  displayValue,
  metricLabel,
  reasonLabel,
  refs,
  useDetailRead,
} from './shared';

export function counterpartCandidateIds(forecast: Forecast, page: ForecastWatchPage): string[] {
  const ids = [
    ...new Set(
      page.items
        .filter(
          ({ watch }) =>
            watch.origin === forecast.origin &&
            watch.areaId === forecast.areaId &&
            watch.forecastIds.includes(forecast.forecastId),
        )
        .flatMap(({ watch }) => watch.forecastIds)
        .filter((id) => id !== forecast.forecastId && /^forecast-[a-f0-9]{64}$/.test(id)),
    ),
  ].sort();
  return ids.length <= 4 ? ids : [];
}
export function verifiedMetricCounterparts(
  forecast: Forecast,
  candidates: readonly Forecast[],
): Partial<Record<ForecastMetric, Forecast>> {
  const valid = candidates.filter(
    (candidate) =>
      candidate.forecastId !== forecast.forecastId &&
      candidate.metric !== forecast.metric &&
      candidate.origin === forecast.origin &&
      candidate.areaId === forecast.areaId &&
      candidate.instanceId === forecast.instanceId &&
      candidate.profileVersion === forecast.profileVersion &&
      candidate.algorithmVersion === forecast.algorithmVersion,
  );
  const matches: Partial<Record<ForecastMetric, Forecast>> = {};
  for (const candidate of valid)
    if (valid.filter((other) => other.metric === candidate.metric).length === 1)
      matches[candidate.metric] = candidate;
  return matches;
}
export function metricNavigation(
  forecast: Forecast,
  metric: ForecastMetric,
  counterpart: Forecast | undefined,
  areaId: string,
  horizon?: number,
): string {
  const selected =
    metric === forecast.metric
      ? forecast
      : counterpart && verifiedMetricCounterparts(forecast, [counterpart])[metric];
  if (selected)
    return riskHref(
      `/forecasts/${selected.forecastId}`,
      areaId,
      horizon === undefined ? {} : { horizon: String(horizon) },
    );
  return riskHref('/evaluation', areaId || forecast.areaId, { metric });
}
function ForecastMetricSelector({
  forecast,
  watches,
  forecastPort,
  areaId,
  horizon,
}: {
  readonly forecast: Forecast;
  readonly watches: ForecastWatchPage | null;
  readonly forecastPort: ForecastPort;
  readonly areaId: string;
  readonly horizon?: number;
}) {
  const identity = watches ? counterpartCandidateIds(forecast, watches).join(',') : '';
  const available = watches !== null;
  const cache = useRef(new Map<string, Forecast>());
  const [attempt, setAttempt] = useState(0);
  const [pairing, setPairing] = useState<{
    key: string;
    status: 'LOADING' | 'READY' | 'ERROR';
    forecasts: Forecast[];
  }>({ key: identity, status: 'LOADING', forecasts: [] });
  useEffect(() => {
    if (!available) return;
    let alive = true;
    const controller = new AbortController();
    setPairing({ key: identity, status: 'LOADING', forecasts: [] });
    async function load() {
      try {
        const forecasts = await Promise.all(
          identity
            ? identity.split(',').map(async (id) => {
                const saved = cache.current.get(id);
                if (saved) return saved;
                const detail = await forecastPort.detail(id, controller.signal);
                if (detail.forecast.forecastId !== id) throw { kind: 'WIRE' };
                cache.current.set(id, detail.forecast);
                return detail.forecast;
              })
            : [],
        );
        if (alive) setPairing({ key: identity, status: 'READY', forecasts });
      } catch {
        if (alive) setPairing({ key: identity, status: 'ERROR', forecasts: [] });
      }
    }
    void load();
    return () => {
      alive = false;
      controller.abort();
    };
  }, [identity, available, forecastPort, attempt]);
  const ready = available && pairing.key === identity && pairing.status === 'READY';
  const counterparts = ready ? verifiedMetricCounterparts(forecast, pairing.forecasts) : {};
  const alternative: ForecastMetric =
    forecast.metric === 'TEMPERATURE' ? 'PARTICULATE' : 'TEMPERATURE';
  const matched = counterparts[alternative];
  const loading = available && (pairing.key !== identity || pairing.status === 'LOADING');
  return (
    <div data-nm-forecast-metric-switch>
      <div className="nm-vs04-controls">
        <label>
          预测指标
          <select
            aria-label="预测指标展示"
            value={forecast.metric}
            disabled={loading}
            onChange={(event) => {
              window.location.assign(
                metricNavigation(
                  forecast,
                  event.target.value as ForecastMetric,
                  counterparts[event.target.value as ForecastMetric],
                  areaId,
                  horizon,
                ),
              );
            }}
          >
            <option value={forecast.metric}>
              {metricLabel(forecast.metric)} / {forecast.unit} · 当前快照
            </option>
            <option value={alternative}>
              {metricLabel(alternative)} / {alternative === 'TEMPERATURE' ? 'degC' : 'ug/m3'} ·{' '}
              {matched ? '同生成时刻快照' : '区域保存历史'}
            </option>
          </select>
        </label>
        <a href={riskHref('/evaluation', areaId || forecast.areaId, { metric: forecast.metric })}>
          浏览其他指标与保存预测 →
        </a>
      </div>
      <p className="nm-vs04-muted" role="status">
        {loading
          ? '正在核验后端关注中的关联预测…'
          : matched
            ? `已核验同区域、同生成时刻的${metricLabel(alternative)}快照：来源 ${matched.sourceId} · 输入截止 ${matched.origin} · epoch ${matched.epoch}。选择指标会打开该保存详情。`
            : !available || pairing.status === 'ERROR'
              ? '关联预测当前不可查询；选择另一指标会进入本区域的真实保存历史。'
              : `未查询到同生成时刻的${metricLabel(alternative)}配对快照；选择该指标会进入本区域的真实保存历史，不能将其他时刻的结果拼入当前预测。`}
      </p>
      {pairing.status === 'ERROR' && (
        <button type="button" onClick={() => setAttempt((n) => n + 1)}>
          重试关联指标查询
        </button>
      )}
    </div>
  );
}
type DisplayModel = 'BOTH' | 'LOCAL_LINEAR_V1' | 'LAST_VALUE_V1';
export function immutableForecastDetail(
  saved: Forecast | null,
  next: ForecastDetail,
  id: string,
): ForecastDetail {
  if (next.forecast.forecastId !== id) throw { kind: 'WIRE' };
  if (saved && JSON.stringify(saved) !== JSON.stringify(next.forecast))
    throw { kind: 'WIRE', code: 'IMMUTABLE_SNAPSHOT_CHANGED' };
  return { ...next, forecast: saved ?? next.forecast };
}
export function ForecastSnapshotChart({
  data,
  model = 'BOTH',
  horizon,
}: {
  readonly data: ForecastDetail;
  readonly model?: DisplayModel;
  readonly horizon?: number;
}) {
  const f = data.forecast;
  const points = f.points.filter((p) => horizon === undefined || p.horizonSeconds === horizon);
  const actuals = data.evaluations.filter(
    (e) =>
      e.status === 'EVALUATED' &&
      e.actualValue !== null &&
      e.actualObservedAt !== null &&
      (horizon === undefined || e.horizonSeconds === horizon),
  );
  const origin = Date.parse(f.origin);
  const times = [
    ...f.inputs.map((s) => Date.parse(s.observedAt)),
    origin,
    ...points.map((p) => Date.parse(p.targetAt)),
    ...actuals.map((e) => Date.parse(e.actualObservedAt!)),
  ];
  const numbers = [
    ...f.inputs.map((s) => s.value),
    ...points.flatMap((p) => [p.localLinearValue, p.lastValue]),
    ...actuals.map((e) => e.actualValue!),
  ];
  if (!numbers.length) return <p>本预测未保存可绘制数值。发布状态：{f.publishStatus}</p>;
  const minTime = Math.min(...times),
    maxTime = Math.max(...times);
  const minValue = Math.min(...numbers),
    maxValue = Math.max(...numbers),
    pad = Math.max((maxValue - minValue) * 0.12, 1);
  const x = (time: number) => 62 + ((time - minTime) / Math.max(maxTime - minTime, 1)) * 660;
  const y = (number: number) =>
    232 - ((number - minValue + pad) / (maxValue - minValue + 2 * pad)) * 182;
  const history = [...f.inputs].sort((a, b) => Date.parse(a.observedAt) - Date.parse(b.observedAt));
  const latest = history[history.length - 1];
  const anchor = latest ? `${x(Date.parse(latest.observedAt))},${y(latest.value)} ` : '';
  const pointLine = (key: 'localLinearValue' | 'lastValue') =>
    anchor + points.map((p) => `${x(Date.parse(p.targetAt))},${y(p[key])}`).join(' ');
  const timeTicks = [...new Set([minTime, origin, ...points.map((p) => Date.parse(p.targetAt))])];
  return (
    <figure className="nm-vs04-chart">
      <p className="nm-vs04-chart-instruction">
        左右滑动图表可查看完整坐标与目标；使用键盘时，先聚焦图表，再按左右方向键滚动。
      </p>
      <div
        className="nm-vs04-chart-viewport"
        role="region"
        aria-label="预测图表，可水平滚动查看全部坐标与目标"
        tabIndex={0}
      >
        <svg
          viewBox="0 0 780 300"
          role="img"
          aria-label={`${metricLabel(f.metric)} ${f.unit} 历史观测与保存预测；预测生成时刻 ${f.origin}`}
          data-nm-forecast-chart
          data-metric={f.metric}
          data-unit={f.unit}
        >
          <text x="10" y="24">
            {f.unit}
          </text>
          {[0, 1, 2, 3, 4].map((i) => {
            const val = minValue - pad + ((maxValue - minValue + 2 * pad) * i) / 4;
            return (
              <g key={i}>
                <line x1="62" x2="722" y1={y(val)} y2={y(val)} stroke="#e1ebf8" />
                <text x="8" y={y(val) + 4}>
                  {val.toFixed(1)}
                </text>
              </g>
            );
          })}
          <line x1="62" x2="722" y1="232" y2="232" stroke="#acc2dd" />
          <polyline
            data-nm-forecast-history
            points={history.map((s) => `${x(Date.parse(s.observedAt))},${y(s.value)}`).join(' ')}
            fill="none"
            stroke="#267ddc"
            strokeWidth="3"
          />
          {model !== 'LAST_VALUE_V1' && (
            <polyline
              data-nm-forecast-prediction
              points={pointLine('localLinearValue')}
              fill="none"
              stroke="#ec9431"
              strokeWidth="3"
              strokeDasharray="8 5"
            />
          )}
          {model !== 'LOCAL_LINEAR_V1' && (
            <polyline
              data-nm-forecast-benchmark
              points={pointLine('lastValue')}
              fill="none"
              stroke="#9674b3"
              strokeWidth="2"
              strokeDasharray="2 5"
            />
          )}
          {history.map((s) => (
            <circle
              key={s.reference}
              data-nm-forecast-input={s.reference}
              cx={x(Date.parse(s.observedAt))}
              cy={y(s.value)}
              r="3"
              fill="#267ddc"
            >
              <title>{`${s.reference} · ${s.observedAt} · ${s.value} ${f.unit}`}</title>
            </circle>
          ))}
          {points.map((p) => (
            <g key={p.horizonSeconds} data-nm-forecast-target={p.horizonSeconds}>
              {model !== 'LAST_VALUE_V1' && (
                <circle
                  cx={x(Date.parse(p.targetAt))}
                  cy={y(p.localLinearValue)}
                  r="5"
                  fill="#ec9431"
                >
                  <title>{`${p.targetAt} · LOCAL_LINEAR_V1 · ${p.localLinearValue} ${f.unit}`}</title>
                </circle>
              )}
              {model !== 'LOCAL_LINEAR_V1' && (
                <rect
                  x={x(Date.parse(p.targetAt)) - 3}
                  y={y(p.lastValue) - 3}
                  width="6"
                  height="6"
                  fill="#9674b3"
                >
                  <title>{`${p.targetAt} · LAST_VALUE_V1 · ${p.lastValue} ${f.unit}`}</title>
                </rect>
              )}
            </g>
          ))}
          {actuals.map((e) => (
            <path
              key={e.horizonSeconds}
              data-nm-forecast-actual={e.actualRef ?? ''}
              d={`M ${x(Date.parse(e.actualObservedAt!))} ${y(e.actualValue!) - 7} l 7 7 -7 7 -7 -7 Z`}
              fill="#109896"
              stroke="white"
              strokeWidth="1.5"
            >
              <title>{`实际观测 ${e.actualRef} · ${e.actualObservedAt} · ${e.actualValue} ${f.unit}`}</title>
            </path>
          ))}
          <line
            data-nm-forecast-origin
            x1={x(origin)}
            x2={x(origin)}
            y1="37"
            y2="242"
            stroke="#467ed2"
            strokeWidth="2"
            strokeDasharray="4 5"
          >
            <title>{`预测生成时刻 ${f.origin}`}</title>
          </line>
          <text x={Math.min(Math.max(x(origin) + 8, 70), 575)} y="35">
            预测生成时刻
          </text>
          {timeTicks.map((t) => (
            <g key={t}>
              <line x1={x(t)} x2={x(t)} y1="232" y2="237" stroke="#9bb5d4" />
              <text
                x={x(t)}
                y="260"
                textAnchor={t === minTime ? 'start' : t === maxTime ? 'end' : 'middle'}
              >
                {t === origin
                  ? '生成时刻'
                  : `${t > origin ? '+' : ''}${Math.round((t - origin) / 1000)}秒`}
              </text>
            </g>
          ))}
          <text x="62" y="286">
            相对预测生成时刻的时间（秒）
          </text>
        </svg>
      </div>
      <figcaption>
        <div className="nm-vs04-legend">
          <span>历史观测 · 实线</span>
          <span className="future">线性预测 · 虚线圆点</span>
          <span className="baseline">最近值基准 · 点线方点</span>
          <span className="actual">后续实际 · 菱形</span>
        </div>
        <p className="nm-vs04-muted">
          历史与未来来自保存的后端数值；跨生成时刻的连线仅作视觉连接。下表提供完整文本读数。
        </p>
      </figcaption>
    </figure>
  );
}
export function ForecastSnapshotEvidence({
  data,
  areaId,
  model = 'BOTH',
  horizon,
}: {
  readonly data: ForecastDetail;
  readonly areaId: string;
  readonly model?: DisplayModel;
  readonly horizon?: number;
}) {
  const f = data.forecast;
  const points = f.points.filter((p) => horizon === undefined || p.horizonSeconds === horizon);
  const evaluations = data.evaluations.filter(
    (e) => horizon === undefined || e.horizonSeconds === horizon,
  );
  return (
    <div className="nm-vs04-grid">
      <div className="nm-vs04-panel">
        <h3>历史观测与预测轨迹</h3>
        <ForecastSnapshotChart
          data={data}
          model={model}
          {...(horizon === undefined ? {} : { horizon })}
        />
        <div className="nm-vs04-table-wrap">
          <table className="nm-vs04-table">
            <caption>保存的预测、基准与目标时间</caption>
            <thead>
              <tr>
                <th>目标 / 核对截止</th>
                <th>LOCAL_LINEAR_V1</th>
                <th>LAST_VALUE_V1</th>
                <th>合成阈值</th>
              </tr>
            </thead>
            <tbody>
              {points.map((p) => (
                <tr key={p.horizonSeconds}>
                  <td>
                    +{p.horizonSeconds} 秒<small>{p.targetAt}</small>
                    <small>截止 {p.deadlineAt}</small>
                  </td>
                  <td>
                    {p.localLinearValue} {f.unit}
                    <small>越阈值 {String(p.localLinearCrosses)}</small>
                  </td>
                  <td>
                    {p.lastValue} {f.unit}
                    <small>越阈值 {String(p.lastValueCrosses)}</small>
                  </td>
                  <td>
                    {p.threshold} {f.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="nm-vs04-section" data-nm-forecast-evaluations>
          <h3>自动回验与匹配证据</h3>
          <p className="nm-vs04-muted">
            待回验保留为 PENDING；无匹配或周期改变显示 UNAVAILABLE，不计为预测成功。
          </p>
          <div className="nm-vs04-table-wrap">
            <table className="nm-vs04-table">
              <thead>
                <tr>
                  <th>目标与状态</th>
                  <th>实际结果 / 原因</th>
                  <th>模型误差 / 结果</th>
                  <th>基准误差 / 结果</th>
                </tr>
              </thead>
              <tbody>
                {evaluations.map((e) => (
                  <tr
                    key={e.horizonSeconds}
                    data-nm-evaluation-horizon={e.horizonSeconds}
                    data-nm-evaluation-status={e.status}
                  >
                    <td>
                      +{e.horizonSeconds} 秒 · {e.status}
                      <small>{e.targetAt}</small>
                      <small>核对 {e.evaluatedAt ?? '尚未核对'}</small>
                    </td>
                    <td>
                      {displayValue(e.actualValue)} {f.unit}
                      <small>{reasonLabel(e.reason)}</small>
                      <small>{e.actualRef ?? '无匹配实际值'}</small>
                      <small>观测 {e.actualObservedAt ?? '无'}</small>
                      <small>受理 {e.actualReceivedAt ?? '无'}</small>
                      <small>可用 {e.actualAvailableAt ?? '无'}</small>
                      <details>
                        <summary>匹配候选依据</summary>
                        {e.candidates.map((s) => (
                          <p key={s.reference}>
                            {s.reference} · {s.observedAt} · {s.value} {s.unit} · epoch {s.epoch}
                          </p>
                        ))}
                        {!e.candidates.length && <p>无候选记录。</p>}
                      </details>
                    </td>
                    <td>
                      有符号 {displayValue(e.linearSignedError)}
                      <small>
                        绝对 {displayValue(e.linearAbsoluteError)} · 平方{' '}
                        {displayValue(e.linearSquaredError)}
                      </small>
                      <strong>{e.linearOutcome ?? '不可评估'}</strong>
                    </td>
                    <td>
                      有符号 {displayValue(e.lastSignedError)}
                      <small>
                        绝对 {displayValue(e.lastAbsoluteError)} · 平方{' '}
                        {displayValue(e.lastSquaredError)}
                      </small>
                      <strong>{e.lastOutcome ?? '不可评估'}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!evaluations.length && <p>暂无核对结果，不能解释为已验证。</p>}
        </div>
        <details className="nm-vs04-section">
          <summary>生成时历史观测 · 固定输入快照</summary>
          <div className="nm-vs04-table-wrap">
            <table className="nm-vs04-table">
              <thead>
                <tr>
                  <th>原观测引用与来源</th>
                  <th>观测读数 / 质量</th>
                  <th>观测 / 受理 / 可用时间</th>
                  <th>epoch / 水位</th>
                </tr>
              </thead>
              <tbody>
                {f.inputs.map((s) => (
                  <tr key={s.reference}>
                    <td>
                      {s.reference}
                      <small>
                        {s.instanceId} / {s.areaId} / {s.sourceId}
                      </small>
                    </td>
                    <td>
                      {s.value} {s.unit}
                      <small>
                        {s.quality} · Unknown {refs(s.unknowns)}
                      </small>
                    </td>
                    <td>
                      {s.observedAt}
                      <small>受理 {s.receivedAt}</small>
                      <small>可用 {s.availableAt}</small>
                    </td>
                    <td>
                      {s.epoch} / {s.availableSequence}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </div>
      <aside className="nm-vs04-panel">
        <h3>版本与可靠性</h3>
        <Fields
          items={[
            ['来源区域', f.areaId],
            ['来源', f.sourceId],
            ['指标 / 单位', `${metricLabel(f.metric)} / ${f.unit}`],
            ['模型', f.algorithmVersion],
            ['对比基准', f.benchmarkVersion],
            ['生成时刻 / 输入截止', f.origin],
            ['发布时刻', f.issuedAt ?? '未发布'],
            ['持久可查询时间上界', f.persistedAtUpperBound ?? '未确认'],
            ['发布状态', f.publishStatus],
            [
              '当前适用性',
              `${data.applicability.status} · ${reasonLabel(data.applicability.reason)}`,
            ],
            ['适用性变化时刻', data.applicability.changedAt ?? '无'],
            ['输入范围', `${f.sampleCount} 条 / ${f.spanSeconds} 秒`],
            ['配置 / 来源修订 / epoch', `${f.configRevision} / ${f.sourceRevision} / ${f.epoch}`],
            ['配置策略', f.profileVersion],
          ]}
        />
        <p className="nm-vs04-caution">
          VS03 合成持有集评估：NEEDS_IMPROVEMENT。线性模型在 MAE 的 5/6 组、RMSE 的 6/6 组弱于
          LAST_VALUE_V1。不能作为正式消防决策依据。
        </p>
        <a href={riskHref('/evaluation', areaId)}>查看完整模型效果 →</a>
        <details className="nm-vs04-section">
          <summary>输入指纹、水位与模型参数</summary>
          <Fields
            items={[
              ['forecastId', f.forecastId],
              ['输入水位', f.inputWatermark],
              ['输入指纹', f.inputFingerprint],
              ['拟合截距', displayValue(f.intercept)],
              ['每秒斜率', displayValue(f.slopePerSecond)],
              ['最新输入', `${displayValue(f.latestValue)} ${f.unit}`],
              ['模式', f.mode],
            ]}
          />
        </details>
        <details className="nm-vs04-section">
          <summary>预测局限</summary>
          {f.limitations.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
          <p>秒级合成趋势不外推为数小时或数日预测；湿度、风、降雨、植被及雷电不在当前输入内。</p>
        </details>
      </aside>
    </div>
  );
}
function ForecastAvailablePage({
  id,
  horizon,
  forecastPort,
  areaId,
}: {
  readonly id: string;
  readonly horizon?: number;
  readonly forecastPort: ForecastPort;
  readonly areaId: string;
}) {
  const snapshot = useRef<{ id: string; forecast: Forecast } | null>(null);
  const read = useCallback(
    async (signal: AbortSignal) => {
      const next = await forecastPort.detail(id, signal);
      const pinned = immutableForecastDetail(
        snapshot.current?.id === id ? snapshot.current.forecast : null,
        next,
        id,
      );
      snapshot.current = { id, forecast: pinned.forecast };
      return pinned;
    },
    [id, forecastPort],
  );
  const detail = useDetailRead(`forecast:${id}`, read);
  const [model, setModel] = useState<DisplayModel>('BOTH');
  const [offset, setOffset] = useState(0);
  const readWatches = useCallback(
    (signal: AbortSignal) => forecastPort.watches({ forecastId: id, offset, limit: 20 }, signal),
    [forecastPort, id, offset],
  );
  const watches = useDetailRead(`forecast-watches:${id}:${offset}`, readWatches);
  const data = detail.state.status === 'READY' ? detail.state.data : null;
  return (
    <section className="nm-vs04-detail" aria-label="趋势预测详情">
      <DetailReadNotice state={detail.state} retry={detail.retry} label="预测详情" />
      {data && (
        <div data-nm-forecast-detail data-forecast-id={id} data-nm-forecast-id={id}>
          <div className="nm-vs04-hero">
            <span className="nm-vs04-tag">实验性预测关注</span>
            <h2>
              {data.forecast.areaId} · {metricLabel(data.forecast.metric)}趋势预测
            </h2>
            <p className="nm-vs04-muted">
              正在查看生成时保存的预测。自动刷新仅更新后端适用性及回验，预测输入与数值保持固定。
            </p>
            <p>
              保存目标：{data.forecast.points.map((p) => `+${p.horizonSeconds} 秒`).join(' / ')} ·{' '}
              {data.evaluations.some((e) => e.status === 'PENDING')
                ? '存在待回验目标'
                : '查看各目标实际回验状态'}
            </p>
          </div>
          <ForecastMetricSelector
            forecast={data.forecast}
            watches={watches.state.status === 'READY' ? watches.state.data : null}
            forecastPort={forecastPort}
            areaId={areaId}
            {...(horizon === undefined ? {} : { horizon })}
          />
          <div className="nm-vs04-controls">
            <label>
              显示模型
              <select
                aria-label="显示模型"
                value={model}
                onChange={(e) => setModel(e.target.value as DisplayModel)}
              >
                <option value="BOTH">线性模型与最近值基准</option>
                <option value="LOCAL_LINEAR_V1">LOCAL_LINEAR_V1</option>
                <option value="LAST_VALUE_V1">LAST_VALUE_V1 基准</option>
              </select>
            </label>
            <label>
              显示预测目标
              <select
                aria-label="显示预测目标"
                value={horizon ?? ''}
                onChange={(e) =>
                  window.location.assign(
                    riskHref(
                      `/forecasts/${id}`,
                      areaId,
                      e.target.value ? { horizon: e.target.value } : {},
                    ),
                  )
                }
              >
                <option value="">全部目标</option>
                {data.forecast.points.map((p) => (
                  <option key={p.horizonSeconds} value={p.horizonSeconds}>
                    +{p.horizonSeconds} 秒
                  </option>
                ))}
              </select>
            </label>
          </div>
          <ForecastSnapshotEvidence
            data={data}
            areaId={areaId}
            model={model}
            {...(horizon === undefined ? {} : { horizon })}
          />
          <div
            className="nm-vs04-panel nm-vs04-section"
            data-nm-forecast-watch-history
            data-forecast-id={id}
          >
            <h3>预测关注变化与预警关联</h3>
            <p className="nm-vs04-muted">
              仅查询本 forecastId 的实际关注修订。关联关系不自动证明预测提前发现事故。
            </p>
            <DetailReadNotice state={watches.state} retry={watches.retry} label="预测关注历史" />
            {watches.state.status === 'READY' && watches.state.data && (
              <>
                <p>
                  共 {watches.state.data.total} 条关注修订 · 查询时间{' '}
                  {watches.state.data.generatedAt}
                </p>
                {!watches.state.data.items.length && (
                  <p>本预测没有关联的多指标关注；单源预测不等于区域预警。</p>
                )}
                {watches.state.data.items.map(({ watch: w, revision }) => (
                  <article
                    className="nm-vs04-evidence"
                    key={`${w.watchId}/${revision}`}
                    data-nm-watch-id={w.watchId}
                    data-nm-watch-revision={revision}
                  >
                    <strong>
                      {w.areaId} · +{w.horizonSeconds} 秒 · {w.status} · 第 {revision} 版
                    </strong>
                    <p>
                      {reasonLabel(w.reason)} · {w.watchId}
                    </p>
                    <p>
                      生成 {w.origin} · 目标 {w.targetAt} · 修订 {w.updatedAt}
                    </p>
                    <p>依赖来源：{refs(w.sourceIds)}</p>
                    <details>
                      <summary>固定预测依赖</summary>
                      {w.forecastIds.map((fid) => (
                        <p key={fid}>
                          <a
                            href={riskHref(`/forecasts/${fid}`, areaId, {
                              horizon: String(w.horizonSeconds),
                            })}
                          >
                            {fid}
                          </a>
                        </p>
                      ))}
                    </details>
                    <p>
                      {w.activeObservedWarningId ? (
                        <a
                          href={riskHref(
                            `/warnings/${encodeURIComponent(w.activeObservedWarningId)}`,
                            areaId,
                          )}
                        >
                          查看后端关联的当前观测预警 {w.activeObservedWarningId} →
                        </a>
                      ) : (
                        '未记录关联的当前观测预警'
                      )}
                    </p>
                    <p>提前量：未知。当前读取结果未提供经核验的提前量或事故确认。</p>
                    {w.limitations.map((l, i) => (
                      <p key={i} className="nm-vs04-muted">
                        {l}
                      </p>
                    ))}
                  </article>
                ))}
                <div className="nm-vs04-controls">
                  <button
                    type="button"
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - 20))}
                  >
                    上一页关注修订
                  </button>
                  <button
                    type="button"
                    disabled={offset + 20 >= watches.state.data.total}
                    onClick={() => setOffset(offset + 20)}
                  >
                    下一页关注修订
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
export function ForecastDetailPage(
  props: RiskPageProps & { readonly id: string; readonly horizon?: number },
) {
  if (!props.forecastPort) return <p role="status">预测后端未配置，详情不可用。</p>;
  return (
    <ForecastAvailablePage
      key={props.id}
      id={props.id}
      areaId={props.areaId}
      forecastPort={props.forecastPort}
      {...(props.horizon === undefined ? {} : { horizon: props.horizon })}
    />
  );
}
